import {
  getAllIds,
  getCountByStatus,
  useGetAllPhotographies,
} from "@/api/photography/photography";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import NewPhotographyDialog from "@/components/photography/NewPhotographyDialog";
import PhotographyPrintDrawer from "@/components/photography/PhotographyPrintDrawer";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearch } from "@/components/table/AdvancedSearch";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import TableSelectionActions from "@/components/table/TableSelectionActions";
import { DataTable } from "@/components/ui/datatable";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  photographiesAdvancedSearchConfig,
  photographiesColumns,
} from "@/config/dashboard/photographies";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { Photography } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const STORAGE_KEY = "photography-table-preferences";

const flatVisiblePhotographyColumns = photographiesColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const PhotographyDashboard = () => {
  const { t } = useTranslation();

  const {
    pagination,
    setPagination,
    countByStatus,
    currentCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Photography>({
    resourceName: "photography",
    resourceCounter: getCountByStatus,
    statusField: "photosPublicationStatus",
  });

  const navigate = useNavigate();

  const { data } = useGetAllPhotographies(fetchParams);

  const columns: ColumnDef<Photography, any>[] = useMemo(() => {
    return generateTable(photographiesColumns, "photography");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisiblePhotographyColumns;
    }
    return flatVisiblePhotographyColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...photographiesColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...photographiesColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  const [selectionState, setSelectionState] = useState({});

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[32px] font-[600]">{t("dashboardTitlePhotos")}</p>
        <GlobalSearch
          config={photographiesAdvancedSearchConfig}
          data={data ?? []}
        />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSearch
            config={photographiesAdvancedSearchConfig}
            resource="photographies"
          />
          <AdvancedSort config={photographiesAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createPphotography")}
            Dialog={NewPhotographyDialog}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => navigate(`/edit/photography/${row.original.id}`)}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
          }}
          selection={{
            getRowId: (p) => p.id,
            getAllRowIds: () => getAllIds(fetchParams.where),
            state: {
              selectionState,
              setSelectionState,
            },
            enableSelectAll: true,
          }}
        />
        <TableSelectionActions
          selectedRows={selectionState}
          setSelectedRows={setSelectionState}
          selectActions={(selectedIds) => (
            <div className="flex flex-row gap-3 items-center">
              <PhotographyPrintDrawer selectedPhotographiesIds={selectedIds} />
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default PhotographyDashboard;
